import type {Props as StyleProps} from '../WidgetStyles';
import type {PostEditing} from '@wordpress/edit-post';
import {CONFIG} from '../../../globals/config';
import {PanelColorSettings} from '@wordpress/block-editor';
import {ColorIndicator, Panel, PanelBody, SelectControl} from '@wordpress/components';
import {__} from '@wordpress/i18n';
import FontSize from './FontSize';
import {useOpenSection} from '../../../hooks/useOpenSection';

import styles from './section.pcss';
import type {UnionOfArray} from '@lipemat/js-boilerplate/utility';


export type AllFields = {
	current_page_color: string;
	current_page_bg_color: string;
	current_page_font_weight: string;
	current_page_font_size: string;
	current_page_hover_color: string;
	current_page_hover_bg_color: string;
	current_page_parent_color: string;
	current_page_parent_bg_color: string;
	current_page_parent_font_weight: string;
	current_page_parent_font_size: string;
	current_page_parent_hover_color: string;
	current_page_parent_hover_bg_color: string;
	parent_page_color: string;
	parent_page_bg_color: string;
	parent_page_font_weight: string;
	parent_page_font_size: string;
	parent_page_hover_color: string;
	parent_page_hover_bg_color: string;
	child_page_color: string;
	child_page_bg_color: string;
	child_page_font_weight: string;
	child_page_font_size: string;
	child_page_hover_color: string;
	child_page_hover_bg_color: string;
	grandchild_page_color: string;
	grandchild_page_bg_color: string;
	grandchild_page_font_weight: string;
	grandchild_page_font_size: string;
	grandchild_page_hover_color: string;
	grandchild_page_hover_bg_color: string;
};

export type SectionData = {
	label: string;
	prefix: Sections;
}

export type Bullets = {
	circle: string;
	decimal: string;
	disc: string;
	square: string;
	'upper-alpha': string;
	'lower-alpha': string;
	none: string;
}

export type Sections =
	'current_page' |
	'current_page_parent' |
	'parent_page' |
	'child_page' |
	'grandchild_page';

export type Fields = {
	_color: string;
	_bg_color: string;
	_font_weight: string;
	_font_size: string;
	_hover_color: string;
	_hover_bg_color: string;
}

export type FontWeights = {
	[ weight: string ]: string;
}

type Keys = keyof AllFields;

type Props = Omit<StyleProps, 'name'> & {
	post: PostEditing | null,
	section: SectionData;
}

const fontWeights = [ {
	label: __( '- default -', 'advanced-sidebar-menu' ),
	value: '',
} ];
Object.entries( CONFIG.styles.weights ).forEach( ( [ value, label ] ) => {
	fontWeights.push( {value, label} );
} );

const COLOR_FIELDS: Array<keyof Fields> = ( Object.keys( CONFIG.styles.fields ) as Array<keyof Fields> )
	.filter( field => field.includes( 'color' ) );

/**
 * Section of settings for a menu level.
 *
 * 1. Current Item.
 * 2. Current Item Parent.
 * 3. Highest Level Parent.
 * 4. Child.
 * 5. Grandchild.
 */
const Section = ( {attributes, setAttributes, section, post}: Props ) => {
	const [ isOpen, toggleOpen ] = useOpenSection( attributes, setAttributes, section.prefix );


	return ( <Panel className={styles.panel}>
		<PanelBody
			title={<span className={styles.title}>
				{section.label}
				{! isOpen && <span className={styles.indicators}>
					{COLOR_FIELDS.map( ( field: UnionOfArray<typeof COLOR_FIELDS> ) => (
						<ColorIndicator
							key={field}
							className={styles.swatch}
							colorValue={attributes[ section.prefix + field as Keys ]} />
					) )}
				</span>}
			</span>}
			opened={isOpen}
			onToggle={toggleOpen}
		>
			{post && 'current_page' === section.prefix && 0 === post.parent &&
				<p className={'description'}>
					{__( 'Overrides Highest Level Parent', 'advanced-sidebar-menu' )}
				</p>}
			{post && 'parent_page' === section.prefix && 0 === post.parent &&
				<p className={'description'}>
					{__( 'Overridden by Current Item', 'advanced-sidebar-menu' )}
				</p>}
			<PanelColorSettings
				className={styles.colors}
				colorSettings={COLOR_FIELDS.map( ( field: UnionOfArray<typeof COLOR_FIELDS> ) => {
					return {
						value: attributes[ section.prefix + field as Keys ],
						onChange: color => setAttributes( {
							[ section.prefix + field ]: color,
						} ),
						label: CONFIG.styles.fields[ field ],
					};
				} )}
			/>
			<SelectControl
				label={__( 'Font weight', 'advanced-sidebar-menu' )}
				labelPosition={'side'}
				value={attributes[ section.prefix + '_font_weight' as Keys ] ?? ''}
				options={fontWeights}
				onChange={value => {
					setAttributes( {
						[ section.prefix + '_font_weight' ]: value,
					} );
				}}
			/>
			<FontSize
				label={__( 'Font Size', 'advanced-sidebar-menu' )}
				value={attributes[ section.prefix + '_font_size' as Keys ] ?? ''}
				onChange={value => {
					setAttributes( {
						[ section.prefix + '_font_size' ]: value,
					} );
				}}
			/>
			<span className={styles.spacer} />
		</PanelBody>
	</Panel> );
};

export default Section;
